import { Controller } from 'react-hook-form'
import React from 'react'
import { Loader2 } from 'lucide-react'

import { InputType } from 'src/types'

import { useAlertMaintenanceUpdateWindow } from 'src/features/AlertMaintenanceUpdateWindow/useAlertMaintenanceUpdateWindow'
import { AlertUpdateMaintenanceWindowProps } from 'src/features/AlertMaintenanceUpdateWindow/types'
import { Textarea } from 'src/components/shadcn/textarea'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from 'src/components/shadcn/select'
import { ScrollArea } from 'src/components/shadcn/scroll-area'
import { Input } from 'src/components/shadcn/input'
import { Button } from 'src/components/shadcn/button'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'src/components/shadcn/alert-dialog'

export const AlertMaintenanceUpdateWindow = ({
  itemId,
  isOpen,
  toggle,
  date,
  address,
  serviceType,
  details,
  odometer,
  serviceCost,
  distanceUnit,
  currency,
}: AlertUpdateMaintenanceWindowProps) => {
  const {
    fieldsLocale,
    title,
    updateBtn,
    cancel,
    fields,
    formattedTitle,
    control,
    maintenanceSelectOptions,
    errors,
    isLoadingGeneral,
    handleSubmit,
    onUpdate,
  } = useAlertMaintenanceUpdateWindow({
    itemId,
    toggle,
    date,
    address,
    odometer,
    serviceType,
    details,
    serviceCost,
    distanceUnit,
    currency,
  })

  return (
    <form onSubmit={handleSubmit(onUpdate)}>
      <AlertDialog open={isOpen}>
        <AlertDialogContent className="max-h-[90svh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-center">{title}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            <ScrollArea className="h-[100svh] md:h-[65svh] w-full">
              <div>
                {fields.map(({ name, label, inputType, placeholder }) =>
                  inputType === InputType.SELECT ? (
                    <div key={name}>
                      <h1 className="mt-5 text-lg font-medium mb-2">
                        {formattedTitle(name, label)}
                      </h1>
                      <Controller
                        name={name}
                        control={control}
                        rules={{ required: fieldsLocale.requiredField }}
                        render={({ field }) => (
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={placeholder} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <SelectLabel>{label}</SelectLabel>
                                {maintenanceSelectOptions.map(
                                  ({ value, placeholder }) => (
                                    <SelectItem key={value} value={value}>
                                      {placeholder}
                                    </SelectItem>
                                  ),
                                )}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.serviceType && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.serviceType.message}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div key={name}>
                      <h1 className="mt-5 text-lg font-medium mb-2">
                        {formattedTitle(name, label)}
                      </h1>
                      <Controller
                        name={name}
                        control={control}
                        rules={{ required: fieldsLocale.requiredField }}
                        render={({ field }) =>
                          inputType === InputType.TEXTAREA ? (
                            <Textarea
                              {...field}
                              id={name}
                              placeholder={placeholder}
                            />
                          ) : (
                            <Input
                              {...field}
                              id={name}
                              placeholder={placeholder}
                            />
                          )
                        }
                      />
                      {errors[name] && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors[name]?.message}
                        </p>
                      )}
                    </div>
                  ),
                )}
              </div>
            </ScrollArea>
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="rounded-[10px]"
              onClick={toggle}
              type="button"
            >
              {cancel}
            </AlertDialogCancel>
            <Button className="rounded-[10px]" onClick={onUpdate}>
              {isLoadingGeneral ? (
                <Loader2 className="animate-spin" size={13} />
              ) : (
                updateBtn
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  )
}
